import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { IoTrophyOutline, IoGolfOutline, IoSettingsOutline, IoChevronBackOutline, IoCopyOutline } from "react-icons/io5";
import { MdOutlineAttachMoney, MdMoneyOff } from "react-icons/md";
import { supabase } from "../ReusableComponents/supabaseClient";
import PoolDetailsModal from "./PoolDetailsModal";
import Modal from "../ReusableComponents/Modal";
import StatusBadge from "../ReusableComponents/StatusBadge";
import InviteToPool from "./InviteToPool";
import ManagePoolEntries from "./ManagePoolEntries";
import { canEditRecord } from "./PoolModalHelper";
import { FaTrophy } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";
import { logError } from "../Utilities/errorLogger";
import { useAuth } from "../Utilities/AuthContext";
import { generateLeaderboardUrl, copyToClipboard } from "../Utilities/shareUtils";

const PoolPage = () => {
  const [pool, setPool] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { poolId } = useParams();
  const navigate = useNavigate();
  const { ownerData } = useAuth();
  const loggedInUser = Number(ownerData?.userId || localStorage.getItem("userId"));

  const showModalRef = useRef(false);
  const showInviteModalRef = useRef(false);
  const showManageModalRef = useRef(false);
  useEffect(() => { showModalRef.current = showModal; }, [showModal]);
  useEffect(() => { showInviteModalRef.current = showInviteModal; }, [showInviteModal]);
  useEffect(() => { showManageModalRef.current = showManageModal; }, [showManageModal]);

  const fetchPoolData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: poolData, error: poolError } = await supabase
        .from("pool")
        .select(`
          id, name, owner, tournament_status, number_of_teams, max_players,
          num_player_used_for_scoring, all_players_must_make_cut, tie_breaker,
          current_round, first_tee_time, leaderboard_token,
          tournament_fixture ( tournament_id, season, start_date, name, id ),
          time_zone
        `)
        .eq("id", poolId)
        .single();

      if (poolError) throw poolError;
      if (!poolData) throw new Error("Pool not found");
      setPool(poolData);

      const { data: teamsData, error: teamsError } = await supabase
        .from("team")
        .select(`
          *, player_name,
          owner_details:user(full_name, id),
          line_items:teamLineItem (count)
        `)
        .eq("pool", poolId)
        .order("rank", { ascending: true, nullsLast: true });

      if (teamsError) throw teamsError;

      const { data: inviteLinks } = await supabase.from("InviteLink").select("*").eq("pool", poolId);

      const teamsWithStatus = teamsData?.map((team) => {
        const teamInviteLink = inviteLinks?.find((link) => link.person === team.owner_details?.id || link.email === team.email);
        const isActive = team.line_items[0]?.count === poolData.max_players && teamInviteLink?.paid;
        return { ...team, isActive };
      }) || [];

      const sortedTeams = teamsWithStatus.sort((a, b) => {
        if (a.owner_details?.id === loggedInUser) return -1;
        if (b.owner_details?.id === loggedInUser) return 1;
        if (a.rank === null) return 1;
        if (b.rank === null) return -1;
        return a.rank - b.rank;
      });

      setTeams(sortedTeams);
    } catch (err) {
      await logError({ filePath: "Pool/PoolPage.js", functionName: "fetchPoolData", errorType: "Fetch Error", errorMessage: err.message, stackTrace: err.stack, requestData: { poolId } });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [poolId, loggedInUser]);

  useEffect(() => {
    fetchPoolData();
    const anyModalOpen = () => showModalRef.current || showInviteModalRef.current || showManageModalRef.current;
    const channel = supabase
      .channel("poolPageChanges")
      .on("postgres_changes", { event: "*", schema: "public", table: "InviteLink", filter: `pool=eq.${poolId}` }, (payload) => {
        if (payload.eventType === "UPDATE" && payload.new?.paid !== payload.old?.paid) {
          setTeams(ct => ct.map(t => t.owner_details?.id === payload.new.person ? { ...t, isActive: payload.new.paid } : t));
        } else if (!anyModalOpen()) fetchPoolData();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "team", filter: `pool=eq.${poolId}` }, () => { if (!anyModalOpen()) fetchPoolData(); });
    channel.subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [poolId, fetchPoolData]);

  const handleCopyLeaderboard = async () => {
    if (pool?.leaderboard_token) {
      const url = generateLeaderboardUrl(pool.leaderboard_token);
      const success = await copyToClipboard(url);
      if (success) toast.success("Leaderboard link copied!", { theme: "dark", autoClose: 2000 });
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/2" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-16 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error || !pool) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <p className="text-red-600">{error || "Pool not found"}</p>
        </div>
      </div>
    );
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A";

  const stats = [
    { label: "Start Date", value: formatDate(pool.tournament_fixture?.start_date) },
    { label: "Teams", value: teams.length },
    { label: "Players/Team", value: pool.max_players || 0 },
    { label: "Scored", value: pool.num_player_used_for_scoring || 0 },
    { label: "Cut Rule", value: pool.all_players_must_make_cut ? "All Make Cut" : "No Cut" },
    { label: "Tie Breaker", value: pool.tie_breaker === "Total Score" ? "Winning Score" : pool.tie_breaker },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20 mb-24 md:mb-6">
      <ToastContainer position="top-center" theme="dark" />

      {/* Header Card */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-green-200 hover:text-white transition-colors">
              <IoChevronBackOutline className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-white tracking-tight">{pool.name}</h1>
          </div>
          <StatusBadge status={pool.tournament_status || "Not Started"} />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
        <Link to={`/pool/${poolId}/team/${teams[0]?.id}`} className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-medium py-2 px-4 rounded-xl transition-all text-sm backdrop-blur-sm">
          <IoGolfOutline className="w-4 h-4" /> My Team
        </Link>
        <Link to={`/leaderboard/${poolId}`} className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-medium py-2 px-4 rounded-xl transition-all text-sm backdrop-blur-sm">
          <FaTrophy className="w-3.5 h-3.5" /> Leaderboard
        </Link>
        {pool.leaderboard_token && (
          <button onClick={handleCopyLeaderboard} className="flex items-center justify-center w-9 h-9 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all backdrop-blur-sm" title="Copy public leaderboard link">
            <IoCopyOutline className="w-4 h-4" />
          </button>
        )}
        {pool.owner === loggedInUser && (
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-white text-primary font-semibold py-2 px-4 rounded-xl transition-all text-sm hover:bg-green-50 shadow-sm">
            <IoSettingsOutline className="w-4 h-4" /> Manage
          </button>
        )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{stat.label}</p>
              <p className="text-sm font-bold text-gray-900 mt-1">{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Teams */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Teams</h3>
        </div>
      {teams.length > 0 ? (
        <div className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {teams.map((team) => {
            const displayName = team.player_name || team.owner_details?.full_name || "Unknown";
            const isWinner = team.rank === "1" && pool.tournament_status === "Official";

            return (
              <div
                key={team.id}
                onClick={() => {
                  const timezone = pool.time_zone || "UTC";
                  const now = moment().tz(timezone);
                  const firstTeeTime = pool.first_tee_time ? moment.utc(pool.first_tee_time).tz(timezone) : null;
                  const tournamentStarted = firstTeeTime ? now.isAfter(firstTeeTime) : false;
                  const isOwner = team.owner_details?.id === loggedInUser;
                  if (!tournamentStarted && !isOwner) {
                    toast.info("Team details visible when the tournament begins", { theme: "dark" });
                    return;
                  }
                  navigate(`/pool/${poolId}/team/${team.id}`);
                }}
                className={`bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-all ${isWinner ? "border-yellow-300 ring-1 ring-yellow-200" : "border-gray-200 hover:border-gray-200"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{displayName}</span>
                    {isWinner && <IoTrophyOutline className="w-4 h-4 text-yellow-500" />}
                  </div>
                  {team.isActive
                    ? <MdOutlineAttachMoney className="w-5 h-5 text-green-600" title="Paid" />
                    : <MdMoneyOff className="w-5 h-5 text-gray-300" title="Not Paid" />
                  }
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>
                    Pos: <span className="font-medium text-gray-900">
                      {team.rank === "MC" ? <span className="text-red-600">Cut</span> : team.rank || "--"}
                    </span>
                  </span>
                  <span>
                    Players: <span className={`font-medium ${team.line_items[0]?.count === pool.max_players ? "text-green-600" : "text-amber-600"}`}>
                      {team.line_items[0]?.count || 0}/{pool.max_players}
                    </span>
                  </span>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      ) : (
        <div className="p-8 text-center">
          <p className="text-gray-400">No teams have joined this pool yet.</p>
        </div>
      )}
      </div>

      {showModal && (
        <PoolDetailsModal isOpen={showModal} onClose={() => setShowModal(false)} poolData={pool} mode="edit" loggedInUser={loggedInUser} refreshData={fetchPoolData} onTeamStatusChange={(t) => setTeams(t)} />
      )}
      {showInviteModal && (
        <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} isSubmitting={isSubmitting}>
          <InviteToPool poolId={poolId} userId={loggedInUser} />
        </Modal>
      )}
      {showManageModal && (
        <Modal isOpen={showManageModal} onClose={() => setShowManageModal(false)} isSubmitting={isSubmitting}>
          <ManagePoolEntries poolId={poolId} canEdit={canEditRecord(pool)} onTeamStatusChange={(t) => setTeams(t)} />
        </Modal>
      )}
    </div>
  );
};

export default PoolPage;
